import {WorldBox, WorldVector} from '@buildinginformationcloud/bic-bim-viewer';
import {ChatViewerSection} from './bim-viewer-chat.model';

/** Eine Schnittebene: sichtbar bleibt die Seite, in die `normal` zeigt (three.js-Konvention). */
export interface SectionPlane {
  normal: WorldVector;
  point: WorldVector;
}

/** Schnitthöhe eines Grundrisses über der Unterkante, wenn chatBIC keine nennt. */
export const DEFAULT_PLAN_HEIGHT = 1.2;

/** Abstand einer Schnittbox um das Ziel, wenn chatBIC keinen nennt. */
export const DEFAULT_BOX_PADDING = 0.3;

/**
 * Die Ebenen eines Schnitts um eine Hülle in Weltkoordinaten.
 *
 * <p>Die Szene hat y nach oben; die IFC-Achsen x/y liegen in der Szene auf x und z (IFC-y zeigt
 * in Richtung −z). Für einen senkrechten Schnitt „quer zur y-Achse" ist die Ebene deshalb eine
 * Ebene mit z-Normale.
 *
 * <ul>
 *   <li>`plan`: eine waagerechte Ebene auf Unterkante + Höhe; alles darüber fällt weg.</li>
 *   <li>`box`: sechs Ebenen um die Hülle, Normalen nach innen.</li>
 *   <li>`vertical`: eine senkrechte Ebene durch die Mitte; die vordere Hälfte fällt weg.</li>
 * </ul>
 */
export function sectionPlanes(box: WorldBox, section: ChatViewerSection): SectionPlane[] {
  const center = {
    x: (box.min.x + box.max.x) / 2,
    y: (box.min.y + box.max.y) / 2,
    z: (box.min.z + box.max.z) / 2,
  };

  switch (section.kind) {
    case 'plan': {
      const height = box.min.y + (section.height ?? DEFAULT_PLAN_HEIGHT);
      return [{normal: {x: 0, y: -1, z: 0}, point: {...center, y: height}}];
    }
    case 'box': {
      const p = section.padding ?? DEFAULT_BOX_PADDING;
      return [
        {normal: {x: 1, y: 0, z: 0}, point: {...center, x: box.min.x - p}},
        {normal: {x: -1, y: 0, z: 0}, point: {...center, x: box.max.x + p}},
        {normal: {x: 0, y: 1, z: 0}, point: {...center, y: box.min.y - p}},
        {normal: {x: 0, y: -1, z: 0}, point: {...center, y: box.max.y + p}},
        {normal: {x: 0, y: 0, z: 1}, point: {...center, z: box.min.z - p}},
        {normal: {x: 0, y: 0, z: -1}, point: {...center, z: box.max.z + p}},
      ];
    }
    case 'vertical':
      return section.axis === 'y'
        ? [{normal: {x: 0, y: 0, z: -1}, point: center}]
        : [{normal: {x: -1, y: 0, z: 0}, point: center}];
  }
}
